import { useMemo } from 'react';
import type { SelectOption } from '_atoms/Select';
import type { JobInfo } from 'store/reducers/jobReducer';
import type { ServiceSimple, User } from 'store/reducers/types';

interface ServiceWithRelations extends ServiceSimple {
  resource_manager: { uuid: string; name: string };
  service_head?: { uuid: string; full_name: string };
  company: { uuid: string; name: string };
}

interface JobRecruiterRef {
  uuid: string;
  full_name: string;
  is_active_responsible_recruiter?: boolean;
}

export interface JobFormValues {
  name: string;
  service: SelectOption;
  company?: SelectOption;
  description?: string;
  description_initial?: string;
  benefits?: string;
  benefits_initial?: string;
  team_lead: SelectOption;
  recruiters?: SelectOption[];
  recruiter_leads?: SelectOption[];
  resource_manager?: SelectOption;
  service_head?: SelectOption;
  reassign_recruiters?: JobRecruiterRef[] | Array<{ old_recruiter: string; new_recruiter: string }> | null;
}

export const dataMapper = (data: JobInfo, isClone?: boolean) =>
  Object.entries({
    name: data.name,
    service: { value: data.service?.uuid, label: data.service?.name },
    company: {
      value: (data.company as { uuid?: string } | undefined)?.uuid,
      label: (data.company as { name?: string } | undefined)?.name,
    },
    description: data.description,
    description_initial: data.description,
    benefits: data.benefits,
    benefits_initial: data.benefits,
    team_lead: {
      value: data.team_lead?.uuid,
      label: data.team_lead?.full_name,
    },
    recruiters: isClone ? [] : (data.recruiters ?? []).map(r => ({ value: r.uuid, label: r.full_name })),
    recruiter_leads:
      (data.recruiter_leads as JobRecruiterRef[] | undefined)?.map(r => ({
        value: r.uuid,
        label: r.full_name,
      })) || [],
    resource_manager: {
      value: (data.resource_manager as { uuid?: string } | undefined)?.uuid,
      label: (data.resource_manager as { full_name?: string } | undefined)?.full_name,
    },
    service_head: (data.service as ServiceWithRelations | undefined)?.service_head
      ? {
          value: (data.service as ServiceWithRelations).service_head!.uuid,
          label: (data.service as ServiceWithRelations).service_head!.full_name,
        }
      : { value: undefined, label: 'Service Head is not assigned to the Service' },
    reassign_recruiters: isClone ? [] : data.recruiters ?? [],
  }).map(([name, value]) => ({ name, value }));

export const formDataMapper = (data: JobFormValues) => ({
  ...data,
  service: data.service.value,
  team_lead: data.team_lead.value,
  recruiters: data.recruiters?.map(r => r.value) || [],
  recruiter_leads: data.recruiter_leads?.map(r => r.value) || [],
  benefits: data.benefits ?? data.benefits_initial,
  description_initial: undefined,
  benefits_initial: undefined,
  service_head: undefined,
  resource_manager: undefined,
  company: undefined,
});

export const userMapper = (list: User[]) => list.map(el => ({ value: el.uuid, label: el.full_name }));

export const useServiceOptions = (services: ServiceSimple[]) =>
  useMemo(
    () =>
      (services as ServiceWithRelations[]).map(el => ({
        value: el.uuid,
        label: el.name,
        resource_manager: { value: el.resource_manager.uuid, label: el.resource_manager.name },
        service_head: el.service_head
          ? { value: el.service_head.uuid, label: el.service_head.full_name }
          : { value: undefined, label: 'Service Head is not assigned to the Service' },
        company: { value: el.company.uuid, label: el.company.name },
      })),
    [services]
  );
